import './movement-item-card.scss'
import { faCircleNotch, faUndo, faCheckCircle, faCheck, faTimes } from '@fortawesome/free-solid-svg-icons'
import { defineComponent, PropType } from 'vue'

import { Item, Movement, MovementTypes, LinkableItemLifecycleStates } from '@skinsoft/types'
import { itemSelectors, locationSelectors, movementSelectors } from '@skinsoft/selectors'
import { ASK_FOR_MISSING_LOCATION, STOP_ASKING_FOR_MISSING_LOCATION_TOPIC } from '@skinsoft/bus'
import { MOVEMENT_ITEM_CARD_LOCATION_CHANGED, UPDATE_ITEM_MOVEMENT_PAGE } from '@skinsoft/events'
import { reactiveVueMixin } from '@skinsoft/reactive-vue'
import { ItemCard } from '@skinsoft/ui'
import { LocationName } from '../../../location/location-name'
import { ErrorRibbon } from './error-ribbon'
import { LocationInput } from './location-input'

type State = 'wait-for-identifier' | 'wait-for-location' | 'validated'

export const MovementItemCard = defineComponent({
    mixins: [reactiveVueMixin],
    components: {
        sErrorRibbon: ErrorRibbon,
        sItemCard: ItemCard,
        sLocationInput: LocationInput,
        sLocationName: LocationName,
    },
    props: {
        commit: {
            type: Function as PropType<() => void>,
            required: true,
        },
        focused: {
            required: false,
        },
        item: {
            type: Object as PropType<Item>,
            required: true,
        },
        movement: {
            type: Object as PropType<Movement>,
            required: true,
        },
        rollback: {
            type: Function as PropType<() => void>,
            required: true,
        },
    },

    template: require('./movement-item-card.html'),
    data() {
        return {
            contentReady: false,
            encounteredError: false,
            undoIcon: faUndo,
            circleNotchIcon: faCircleNotch,
            playIcon: faCheckCircle,
            checkIcon: faCheck,
            crossIcon: faTimes,
            reverting: false,
            state: 'wait-for-identifier' as State,
            isBeingDeleted: false,
            deleting: false,
            shouldAskMissingLocation: false,
            futureLocation: '',
        }
    },
    computed: {
        movementType(): ReturnType<typeof movementSelectors.movementType> {
            return movementSelectors.movementType(this.movement);
        },

        linkableItemState(): ReturnType<typeof movementSelectors.linkableItemState> {
            return movementSelectors.linkableItemState(this.movement, itemSelectors.id(this.item));
        },

        realTimeLocation(): ReturnType<typeof movementSelectors.linkableItemArrivalLocation> {
            return movementSelectors.linkableItemArrivalLocation(
                this.movement,
                itemSelectors.id(this.item),
            );
        },

        canBeReverted(): boolean | ReturnType<typeof movementSelectors.canLinkableItemTransitionTo> | undefined {
            if (this.linkableItemState === LinkableItemLifecycleStates.SELECTED) {
                return false;
            }

            switch (this.movementType) {
                case MovementTypes.SIMPLE:
                    return movementSelectors.canLinkableItemTransitionTo(
                        this.movement,
                        itemSelectors.id(this.item),
                        LinkableItemLifecycleStates.SELECTED,
                    );

                case MovementTypes.COMPLEX:
                case MovementTypes.RETURN:
                    return (
                        movementSelectors.canLinkableItemTransitionTo(
                            this.movement,
                            itemSelectors.id(this.item),
                            LinkableItemLifecycleStates.SELECTED,
                        ) ||
                        movementSelectors.canLinkableItemTransitionTo(
                            this.movement,
                            itemSelectors.id(this.item),
                            LinkableItemLifecycleStates.IN_TRANSIT,
                        )
                    );
            }
        },
    },
    mounted() {
        this.subscribeToBus(ASK_FOR_MISSING_LOCATION, (value) => {
            this.askForMissingLocation(value.payload)
        })
        this.subscribeToBus(STOP_ASKING_FOR_MISSING_LOCATION_TOPIC, () => {
            this.shouldAskMissingLocation = false
        })
        this.subscribeToFutureLocation()
    },
    methods: {
        triggerError() {
            this.encounteredError = true;
        },

        syncLinkableItemState() {
            switch (this.linkableItemState) {
                case LinkableItemLifecycleStates.SELECTED:
                    this.state = 'wait-for-identifier';
                    break;
                case LinkableItemLifecycleStates.IN_TRANSIT:
                    if (this.movementType === MovementTypes.SIMPLE) {
                        this.state = 'validated';
                    } else {
                        this.state = 'wait-for-location';
                    }
                    break;
                case LinkableItemLifecycleStates.FINALIZED:
                    this.state = 'validated';
                    break;
            }
        },

        async revertItem() {
            this.waitForUserApproval(this.$t('movement.itemCard.revertPrompt') as string);

            this.reverting = true;

            const movementId = movementSelectors.id(this.movement);
            const linkableItemId = itemSelectors.id(this.item);
            try {
                switch (this.movementType) {
                    case MovementTypes.SIMPLE:
                        await this.$movementDataStore.transitionLinkableItem(
                            movementId,
                            linkableItemId,
                            LinkableItemLifecycleStates.SELECTED,
                        );
                        break;
                    case MovementTypes.COMPLEX:
                    case MovementTypes.RETURN:
                        switch (this.linkableItemState) {
                            case LinkableItemLifecycleStates.IN_TRANSIT:
                                await this.$movementDataStore.transitionLinkableItem(
                                    movementId,
                                    linkableItemId,
                                    LinkableItemLifecycleStates.SELECTED,
                                );
                                break;
                            case LinkableItemLifecycleStates.FINALIZED:
                                await this.$movementDataStore.transitionLinkableItem(
                                    movementId,
                                    linkableItemId,
                                    LinkableItemLifecycleStates.IN_TRANSIT,
                                );
                                await this.$movementDataStore.transitionLinkableItem(
                                    movementId,
                                    linkableItemId,
                                    LinkableItemLifecycleStates.SELECTED,
                                );
                                break;
                        }
                        break;
                }
                this.state = 'wait-for-identifier';
                this.$emit('change')
            } catch (error) {
                // tslint:disable-next-line:no-console
                console.error(error);
            } finally {
                this.reverting = false;
            }
        },

        subscribeToFutureLocation() {
            if(this.item) {
                const futureLocationId = itemSelectors.futureLocation(this.item)
                if(futureLocationId) {
                    this.$locationDataStore.fetchOne(futureLocationId).subscribe(location => {
                        this.futureLocation = locationSelectors.fullPath(location, this.getAppLocale())
                    })
                }
            }
        },

        askForMissingLocation(concernedItemsIds: string[]) {
            this.shouldAskMissingLocation = concernedItemsIds.includes(this.item.id)
        },

        onLocationChanged(newLocation) {
            if(this.shouldAskMissingLocation) {
                const payload = {
                    itemId: this.item.id,
                    changes: {
                        locationItemSchema: {
                            permanentLocation: {
                                permanentLocation: newLocation.id,
                            },
                        },
                    },
                }
                this.$emit(UPDATE_ITEM_MOVEMENT_PAGE, payload)
            } else {
                const payload = {
                    itemId: this.item.id,
                    location: newLocation,
                }
                this.$emit(MOVEMENT_ITEM_CARD_LOCATION_CHANGED, payload)
            }
        },

        async startItemTransit() {
            if (this.item && this.linkableItemState === LinkableItemLifecycleStates.SELECTED) {
                await this.$movementDataStore.transitionLinkableItem(this.movement.id, this.item.id, LinkableItemLifecycleStates.IN_TRANSIT)
                this.$emit('change')
            }
        },

        deleteItem() {
            this.deleting = true
            this.$emit('delete', this.item.id)
        },
    },
    watch: {
        focused: {
            handler() {
                this.encounteredError = false;
            },
            immediate: false,
        },

        state: {
            handler(state: State) {
                // as this is not executing on the first render, we are sure this will happen only after a transition
                switch (state) {
                    case 'wait-for-identifier':
                        this.rollback();
                        break;
                    case 'validated':
                        this.commit();
                        break;
                }
            },
            immediate: false,
        },

        item: {
            handler() {
                if (this.item) {
                    this.syncLinkableItemState()
                    this.subscribeToFutureLocation()
                    this.isBeingDeleted = false
                    this.deleting = false
                }
            },
            immediate: true,
            deep: true,
        },
    },
})
